import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/app/hooks'
import { ModalWrapper } from '@/components/atoms/modal-wrapper'
import { SpaceReservationDetail } from '@/features/space/space-reservation-types'
import { buildRefundMessage } from '@/components/pages/mypage/space-reservation-display'
import { useBreakPoint } from '@/hooks/use-break-point'
import { css } from '@emotion/react'

interface SpaceReservationCancelConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  reservation: SpaceReservationDetail
  loading?: boolean
}

export const SpaceReservationCancelConfirmModal: React.FC<SpaceReservationCancelConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  reservation,
  loading = false,
}) => {
  const { t } = useTranslation()
  const { isPc } = useBreakPoint()
  const design = useAppSelector(state => state.design)
  const refundInfo = reservation.refundInfo
  const isAmountShownType = (type: string): boolean =>
    type === 'refund_available' ||
    type === 'refund_available_on_site_settled' ||
    type === 'no_refund_on_site_payment'
  const isZeroRefund =
    refundInfo != null &&
    refundInfo.amount === 0 &&
    isAmountShownType(refundInfo.type)
  const showRefundMessage =
    !isZeroRefund && refundInfo?.type !== 'no_refund_zero'

  const refundMessage =
    refundInfo != null
      ? buildRefundMessage(refundInfo.type, refundInfo.amount, t)
      : t('Refund contact facility fallback')

  const amountStr =
    refundInfo != null ? `¥${refundInfo.amount.toLocaleString()}` : null
  const hasAmount = amountStr != null && refundMessage.includes(amountStr)

  const renderRefundMessage = () => {
    if (hasAmount && amountStr) {
      const [before, after] = refundMessage.split(amountStr)
      return (
        <>
          {before}
          <span css={amountHighlight}>{amountStr}</span>
          {after}
        </>
      )
    }
    return refundMessage
  }

  return (
    <ModalWrapper
      isOpen={isOpen}
      modalStyle={{
        height: 'auto',
        maxHeight: showRefundMessage ? 209 : 150,
        padding: '20px 0 16px 0',
        width: isPc ? '343px' : 'auto',
        overflowY: 'auto',
        boxShadow: '0px 2px 10px #00000029',
        borderRadius: '6px',
      }}
      onClose={onClose}
    >
      <div css={contentStyle}>
        <h2 css={modalTitle} style={{ color: design?.text.textColor }}>
          {t('Cancel reservation?')}
        </h2>
        {showRefundMessage && (
          <p css={refundMessageStyle} style={{ color: design?.text.textColor }}>
            {renderRefundMessage()}
          </p>
        )}
        <div css={buttonWrapperStyle}>
          <button
            type="button"
            css={[buttonBase, backButtonStyle]}
            onClick={onClose}
            disabled={loading}
          >
            {t('Go back')}
          </button>
          <button
            type="button"
            css={[buttonBase, confirmButtonStyle]}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? t('Loading...') : t('Confirm cancel')}
          </button>
        </div>
      </div>
    </ModalWrapper>
  )
}

const contentStyle = css({
  textAlign: 'center',
  paddingInline: '24px',
})

const modalTitle = css({
  margin: '0 0 1rem',
  fontSize: '18px',
  lineHeight: '28px',
  fontWeight: 'bold',
  letterSpacing: '0.6px',
  color: '#272727',
})

const refundMessageStyle = css({
  margin: '0 0 1.5rem',
  fontSize: '14px',
  lineHeight: '21px',
  fontWeight: 500,
  letterSpacing: '1.4px',
  color: '#272727',
})

const amountHighlight = css({
  color: '#A59785',
})

const buttonWrapperStyle = css({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: '0.5rem',
})

const buttonBase = css({
  width: '148px',
  height: '48px',
  padding: 0,
  fontSize: '16px',
  lineHeight: '32px',
  fontWeight: 500,
  letterSpacing: '0.8px',
  borderRadius: '6px',
  cursor: 'pointer',
  border: 'none',
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
})

const backButtonStyle = css({
  background: '#FFFFFF 0% 0% no-repeat padding-box',
  border: '1px solid #A59785',
  color: '#A59785',
})

const confirmButtonStyle = css({
  background: '#A59785 0% 0% no-repeat padding-box',
  color: '#FFFFFF',
})
